import React, { Component } from "react"
import { Alert, Button, Form, Modal, OverlayTrigger, Spinner, Table, Tooltip } from "react-bootstrap"
import moment from "moment"
import snackBar from "src/utilities/snackBar/SnackBar"
import { getVault77ErrorMessage } from "src/components/vault77/core/apiError"
import { formatForDisplay } from "src/components/vault77/core/MaskedDataField"
import {
    getPayRateHistoryList,
    createPayRateHistory,
    updatePayRateHistory,
    deletePayRateHistory,
} from "src/components/vault77/payRateHistory/api/payRateHistory.api"
import type { PayRateHistory } from "src/components/vault77/payRateHistory/types"
import { HealthRefreshContext, type HealthRefreshContextValue } from "src/components/vault77/health/HealthRefreshContext"
import AccessControl from "src/components/common/AccessControl"
import "./PayRateModal.css"

interface PayRateModalProps {
    employeeId: number
    onChanged: () => void
    onClose: () => void
    readOnly?: boolean
    // When opened from a review to record its pay change, link the created (or edited)
    // row back to that review via SourceReviewId, so the review's "unrecorded pay change"
    // warning clears. Undefined for the standalone UserAdmin usage (no behavior change).
    sourceReviewId?: number | null
    // Optional values to prefill the add form with (e.g. the review's proposed pay/date),
    // and to highlight the row whose effective date matches.
    prefill?: { PayRate?: string; EffectiveDate?: string; PayType?: string }
    // When true AND the employee has no rates yet, open straight into a prefilled add
    // form (the "fix an unrecorded pay change" flow). When false/undefined the modal
    // opens in list mode (plain "view pay rate history").
    autoCreateWhenEmpty?: boolean
}

interface PayRateForm {
    PayRate: string
    EffectiveDate: string
    PayType: string
    Comment: string
}

type Mode = "list" | "form"

type PayRateModalState = {
    list: PayRateHistory[]
    loading: boolean
    saving: boolean
    deleting: boolean
    mode: Mode
    editing: PayRateHistory | null
    form: PayRateForm
    error: string | null
}

const EMPTY_FORM: PayRateForm = { PayRate: "", EffectiveDate: "", PayType: "", Comment: "" }

// PayRate column is DECIMAL(10,2): up to 8 digits before the decimal and 2 after
// (max 99,999,999.99). Cap integer and decimal digits so the value always fits.
function sanitizePayRate(value: string): string {
    const cleaned = value.replace(/[^\d.]/g, "")
    const firstDot = cleaned.indexOf(".")
    if (firstDot === -1) {
        return cleaned.slice(0, 8)
    }
    const whole = cleaned.slice(0, firstDot).slice(0, 8)
    const decimals = cleaned.slice(firstDot + 1).replace(/\./g, "").slice(0, 2)
    return `${whole}.${decimals}`
}

class PayRateModal extends Component<PayRateModalProps, PayRateModalState> {

    // Lets a successful pay-rate change re-run the Employees-page health scan (gauges + row warnings)
    // without a reload. No-op outside the employee detail panel (see HealthRefreshContext).
    static contextType = HealthRefreshContext

    state: PayRateModalState = {
        list: [],
        loading: true,
        saving: false,
        deleting: false,
        mode: "list",
        editing: null,
        form: { ...EMPTY_FORM },
        error: null,
    }

    componentDidMount() {
        this.loadList()
    }

    loadList = async () => {
        this.setState({ loading: true, error: null })
        try {
            const list = await getPayRateHistoryList({ employeeId: this.props.employeeId })
            list.sort((a, b) => new Date(b.EffectiveDate).getTime() - new Date(a.EffectiveDate).getTime())
            // Opened from a review with nothing on record yet → jump straight to a
            // prefilled add form so HR can record the pay change in one step.
            const openPrefilled = list.length === 0 && !this.props.readOnly && !!this.props.prefill && !!this.props.autoCreateWhenEmpty
            this.setState({
                list,
                loading: false,
                ...(openPrefilled
                    ? { mode: "form", editing: null, form: { ...EMPTY_FORM, ...this.props.prefill } }
                    : {}),
            })
        } catch (err) {
            this.setState({ loading: false, error: getVault77ErrorMessage(err, "Unable to load pay rate history.") })
        }
    }

    startCreate = () => {
        this.setState({ mode: "form", editing: null, form: { ...EMPTY_FORM, ...(this.props.prefill || {}) }, error: null })
    }

    // Attach an EXISTING pay-rate row to the review this modal was opened from.
    // Sets only SourceReviewId (a minimal update) so HR doesn't have to re-enter pay/date.
    // Used for legacy/migrated rates that match a review but were never linked.
    linkToReview = async (row: PayRateHistory) => {
        const reviewId = this.props.sourceReviewId
        if (this.props.readOnly || reviewId == null) return
        if (row.SourceReviewId === reviewId) return
        if (row.SourceReviewId != null && row.SourceReviewId !== reviewId) {
            if (!window.confirm("This pay rate is already linked to another review. Re-link it to this review?")) return
        }
        // Reverse direction: the review may already be linked to a DIFFERENT pay rate. Only one
        // rate can be linked to a review, so linking this one MOVES the link here (the server
        // clears the other automatically). Confirm so it isn't a surprise.
        const otherLinked = this.state.list.find(
            (r) => r.SourceReviewId === reviewId && r.PayRateHistoryId !== row.PayRateHistoryId,
        )
        if (otherLinked) {
            if (!window.confirm("This review is already linked to another pay rate. Linking this one will move the link here. Continue?")) return
        }
        this.setState({ saving: true, error: null })
        try {
            await updatePayRateHistory({
                ...row,
                EndDate: null,
                SourceReviewId: reviewId,
            })
            snackBar({ text: "Pay rate linked to this review", type: "success", timeout: 3000 })
            this.props.onChanged()
            ;(this.context as HealthRefreshContextValue).notifyHealthChanged()
            await this.loadList()
            this.setState({ saving: false })
        } catch (err) {
            this.setState({ saving: false, error: getVault77ErrorMessage(err, "Unable to link pay rate to the review.") })
        }
    }

    startEdit = (row: PayRateHistory) => {
        this.setState({
            mode: "form",
            editing: row,
            error: null,
            form: {
                PayRate: row.PayRate != null ? String(row.PayRate) : "",
                EffectiveDate: row.EffectiveDate ? row.EffectiveDate.slice(0, 10) : "",
                PayType: row.PayType ?? "",
                Comment: row.Comment ?? "",
            },
        })
    }

    handleDelete = async (row: PayRateHistory) => {
        const message = row.SourceReviewId != null
            ? "This pay rate is linked to a review. Deleting it will leave that review with no recorded pay change (it will appear in the health report). Delete anyway?"
            : "Delete this pay rate? This cannot be undone."
        if (!window.confirm(message)) return
        this.setState({ deleting: true, error: null })
        try {
            await deletePayRateHistory(row.PayRateHistoryId)
            snackBar({ text: "Pay rate deleted", type: "success", timeout: 3000 })
            this.props.onChanged()
            ;(this.context as HealthRefreshContextValue).notifyHealthChanged()
            await this.loadList()
            this.setState({ deleting: false })
        } catch (err) {
            this.setState({ deleting: false, error: getVault77ErrorMessage(err, "Unable to delete pay rate.") })
        }
    }

    backToList = () => {
        this.setState({ mode: "list", editing: null, error: null })
    }

    copyComment = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text)
            snackBar({ text: "Comment copied to clipboard", type: "success", timeout: 2000 })
        } catch {
            snackBar({ text: "Unable to copy comment", type: "error", timeout: 3000 })
        }
    }

    handleFormChange = (field: keyof PayRateForm, value: string) => {
        this.setState({ form: { ...this.state.form, [field]: value } })
    }

    validate = (): string | null => {
        const { PayRate, EffectiveDate } = this.state.form
        if (!EffectiveDate) return "Effective date is required."
        const rate = Number(PayRate)
        if (!PayRate || Number.isNaN(rate) || rate <= 0) return "Enter a pay rate greater than 0."
        return null
    }

    handleSave = async () => {
        const validationError = this.validate()
        if (validationError) {
            this.setState({ error: validationError })
            return
        }
        const { editing, form } = this.state
        this.setState({ saving: true, error: null })
        try {
            // EffectiveDate is the single source of truth for the timeline. The client never
            // authors EndDate — it always sends null so the stored end can't drift from /
            // contradict the effective-date ordering (which is what produced the overlapping
            // rows we saw). The span "End" shown in the table is derived from the ordering on
            // read (see renderList), not from this column. EndDate stays nullable server-side.
            if (editing) {
                const { SystemComment: _unusedSystemComment, ...editingWithoutSystemComment } = editing
                await updatePayRateHistory({
                    ...editingWithoutSystemComment,
                    EndDate: null,
                    PayRate: Number(form.PayRate),
                    EffectiveDate: form.EffectiveDate,
                    PayType: form.PayType || null,
                    Comment: form.Comment || null,
                    // Editing never changes the review link — that is done explicitly via
                    // "Link to this review" (linkToReview), so editing an unrelated row in
                    // review context can't silently re-link it. Preserve the row's own value.
                    SourceReviewId: editing.SourceReviewId,
                })
            } else {
                await createPayRateHistory({
                    EmployeeId: this.props.employeeId,
                    EndDate: null,
                    PayRate: Number(form.PayRate),
                    EffectiveDate: form.EffectiveDate,
                    PayType: form.PayType || null,
                    Comment: form.Comment || null,
                    // Link to the originating review when launched from one (null otherwise).
                    SourceReviewId: this.props.sourceReviewId ?? null,
                })
            }
            snackBar({ text: editing ? "Pay rate updated" : "Pay rate created", type: "success", timeout: 3000 })
            this.props.onChanged()
            ;(this.context as HealthRefreshContextValue).notifyHealthChanged()
            await this.loadList()
            this.setState({ saving: false, mode: "list", editing: null })
        } catch (err) {
            // Surfaces auto-supersede / duplicate-effective-date (409) messages from the envelope.
            this.setState({ saving: false, error: getVault77ErrorMessage(err, "Unable to save pay rate.") })
        }
    }

    renderList = () => {
        const { list, loading, saving, deleting } = this.state
        const busy = saving || deleting
        const readOnly = this.props.readOnly
        // Review context (modal opened from a review to record/link its pay change).
        const reviewId = this.props.sourceReviewId ?? null
        const reviewDate = this.props.prefill?.EffectiveDate || null
        if (loading) {
            return <div className="PayRateModalLoading"><Spinner animation="border" variant="secondary" /></div>
        }
        return (
            <>
                {!readOnly && <div className="PayRateModalToolbar">
					<AccessControl target="Vault77.Write.PayRate">
						<OverlayTrigger trigger={["hover", "focus"]} placement="left" overlay={<Tooltip id="prm-add">Add a pay rate</Tooltip>}>
							<span
								className="material-icons PayRateModalAddBtn"
								id="pay-rate-modal-add-btn"
								role="button"
								aria-label="Add a pay rate"
								onClick={this.startCreate}
							>add</span>
						</OverlayTrigger>
					</AccessControl>
                </div>}
                {reviewId != null && list.length > 0 && (
                    <p className="PayRateModalNote">
                        Link the pay rate that resulted from this review — the row matching its effective date is highlighted. Or add a new one.
                    </p>
                )}
                {list.length === 0 ? (
                    <p className="PayRateModalEmpty">No pay rate history for this employee.</p>
                ) : (
                    <Table size="sm" hover responsive className="PayRateModalTable">
                        <thead>
                            <tr>
                                <th>Effective</th>
                                <th>End</th>
                                <th>Rate</th>
                                <th>Comment</th>
                                {/* Pay Type hidden for now */}
                                {/* <th>Type</th> */}
                                {!readOnly && <th className="PayRateModalActionsCol" />}
                            </tr>
                        </thead>
                        <tbody>
                            {list.map((row, index) => {
                                // Span end is DERIVED, not stored: a row runs until the day before the
                                // next-newer rate takes effect. The list is sorted newest-first, so the
                                // newer row is the one above (index - 1); the newest row (index 0) is the
                                // open/current rate with no end ("—"). We never read row.EndDate here.
                                const derivedEnd = index === 0
                                    ? null
                                    : moment(list[index - 1].EffectiveDate).subtract(1, "day")
                                // In review context: flag the row already linked to this review, and
                                // highlight the one whose effective date matches the review's.
                                const linkedHere = reviewId != null && row.SourceReviewId === reviewId
                                const matchesReviewDate =
                                    reviewId != null && !!reviewDate && row.EffectiveDate.slice(0, 10) === reviewDate
                                return (
                                <tr
                                    key={row.PayRateHistoryId}
                                    className={matchesReviewDate && !linkedHere ? "table-warning" : undefined}
                                >
                                    <td>{moment(row.EffectiveDate).format("MM/DD/YYYY")}</td>
                                    <td>{derivedEnd ? derivedEnd.format("MM/DD/YYYY") : "—"}</td>
                                    <td>{formatForDisplay(Number(row.PayRate).toFixed(2), "currency")}</td>
                                    <td className="PayRateModalCommentCell">
                                        {row.Comment || row.SystemComment ? (
                                            <div className="PayRateModalCommentGroup">
                                                {row.Comment ? (
                                                    <div className="PayRateModalComment">
                                                        <span className="PayRateModalCommentText">{row.Comment}</span>
                                                        <OverlayTrigger
                                                            trigger={["hover", "focus"]}
                                                            placement="top"
                                                            overlay={<Tooltip id={`prm-comment-${row.PayRateHistoryId}`}>{row.Comment}</Tooltip>}
                                                        >
                                                            <span
                                                                className="material-icons PayRateModalIcon PayRateModalCommentIcon"
                                                                role="button"
                                                                tabIndex={0}
                                                                aria-label="Copy comment"
                                                                onClick={() => this.copyComment(row.Comment as string)}
                                                                onKeyDown={(e: React.KeyboardEvent) => {
                                                                    if (e.key === "Enter" || e.key === " ") {
                                                                        e.preventDefault()
                                                                        this.copyComment(row.Comment as string)
                                                                    }
                                                                }}
                                                            >content_copy</span>
                                                        </OverlayTrigger>
                                                    </div>
                                                ) : null}
                                                {row.SystemComment ? (
                                                    <OverlayTrigger
                                                        trigger={["hover", "focus"]}
                                                        placement="top"
                                                        overlay={<Tooltip id={`prm-syscomment-${row.PayRateHistoryId}`}>{row.SystemComment}</Tooltip>}
                                                    >
                                                        <span
                                                            className="material-icons PayRateModalIcon PayRateModalSystemCommentIcon"
                                                            role="button"
                                                            tabIndex={0}
                                                            aria-label="Copy system comment"
                                                            onClick={() => this.copyComment(row.SystemComment as string)}
                                                            onKeyDown={(e: React.KeyboardEvent) => {
                                                                if (e.key === "Enter" || e.key === " ") {
                                                                    e.preventDefault()
                                                                    this.copyComment(row.SystemComment as string)
                                                                }
                                                            }}
                                                        >info</span>
                                                    </OverlayTrigger>
                                                ) : null}
                                            </div>
                                        ) : "—"}
                                    </td>
                                    {/* Pay Type hidden for now */}
                                    {/* <td>{row.PayType || "—"}</td> */}
                                    {!readOnly && <td className="PayRateModalActions">
                                        <div className="PayRateModalRowActions">
                                            {reviewId != null && (linkedHere
                                                ? <OverlayTrigger placement="top" overlay={<Tooltip id={`prm-linked-${row.PayRateHistoryId}`}>Linked to this review</Tooltip>}>
                                                    <span className="material-icons PayRateModalIcon PayRateModalIconLinked" role="img" aria-label="Linked to this review">link</span>
                                                </OverlayTrigger>
                                                // linkToReview is a PUT /payRateHistory — the API checks Vault77.Write.PayRate
                                                : <AccessControl target="Vault77.Write.PayRate">
                                                    <OverlayTrigger placement="top" overlay={<Tooltip id={`prm-link-${row.PayRateHistoryId}`}>Link to this review</Tooltip>}>
                                                        <span
                                                            className={`material-icons PayRateModalIcon ${matchesReviewDate ? "PayRateModalIconLinkMatch" : "PayRateModalIconLink"}${busy ? " PayRateModalIconDisabled" : ""}`}
                                                            role="button"
                                                            aria-label="Link to this review"
                                                            onClick={() => { if (!busy) this.linkToReview(row) }}
                                                        >add_link</span>
                                                    </OverlayTrigger>
                                                </AccessControl>
                                            )}
											<AccessControl target="Vault77.Write.PayRate">
												<OverlayTrigger placement="top" overlay={<Tooltip id={`prm-edit-${row.PayRateHistoryId}`}>Edit</Tooltip>}>
													<span
														className={`material-icons PayRateModalIcon PayRateModalIconEdit${busy ? " PayRateModalIconDisabled" : ""}`}
														role="button"
														aria-label="Edit pay rate"
														onClick={() => { if (!busy) this.startEdit(row) }}
													>edit</span>
												</OverlayTrigger>
											</AccessControl>
											{/* DELETE /payRateHistory/{id} — the API checks Vault77.Delete.PayRate, not Write */}
											<AccessControl target="Vault77.Delete.PayRate">
												<OverlayTrigger placement="top" overlay={<Tooltip id={`prm-delete-${row.PayRateHistoryId}`}>Delete</Tooltip>}>
													<span
														className={`material-icons PayRateModalIcon PayRateModalIconDelete${busy ? " PayRateModalIconDisabled" : ""}`}
														role="button"
														aria-label="Delete pay rate"
														onClick={() => { if (!busy) this.handleDelete(row) }}
													>delete</span>
												</OverlayTrigger>
											</AccessControl>
                                        </div>
                                    </td>}
                                </tr>
                                )
                            })}
                        </tbody>
                    </Table>
                )}
            </>
        )
    }

    renderForm = () => {
        const { form, saving, editing } = this.state
        return (
            <Form noValidate>
                <Form.Group className="mb-2">
                    <Form.Label>Effective Date</Form.Label>
                    <Form.Control
                        type="date"
                        value={form.EffectiveDate}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.handleFormChange("EffectiveDate", e.target.value)}
                    />
                </Form.Group>
                <Form.Group className="mb-2">
                    <Form.Label>Pay Rate</Form.Label>
                    <Form.Control
                        type="text"
                        inputMode="decimal"
                        placeholder="0.00"
                        value={form.PayRate}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.handleFormChange("PayRate", sanitizePayRate(e.target.value))}
                    />
                    <Form.Text className="text-muted">Up to 8 digits and 2 decimals (max 99,999,999.99).</Form.Text>
                </Form.Group>
                {/* Pay Type hidden for now — field kept in state/payload, just not shown.
                <Form.Group className="mb-2">
                    <Form.Label>Pay Type</Form.Label>
                    <Form.Control
                        value={form.PayType}
                        placeholder="e.g. Hourly, Salary"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.handleFormChange("PayType", e.target.value)}
                    />
                </Form.Group>
                */}
                <Form.Group className="mb-2">
                    <Form.Label>Comment</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={2}
                        value={form.Comment}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => this.handleFormChange("Comment", e.target.value)}
                    />
                </Form.Group>
                {!editing && <p className="PayRateModalNote">Creating a new rate automatically closes the prior open rate the day before this effective date.</p>}
                <div className="PayRateModalFormActions">
                    <OverlayTrigger trigger={["hover", "focus"]} placement="top" overlay={<Tooltip id="prm-back">Back</Tooltip>}>
                        <span
                            className={`material-icons PayRateModalIcon PayRateModalIconBack${saving ? " PayRateModalIconDisabled" : ""}`}
                            role="button"
                            aria-label="Back"
                            onClick={() => { if (!saving) this.backToList() }}
                        >arrow_back</span>
                    </OverlayTrigger>
					<AccessControl target="Vault77.Write.PayRate">
						<Button variant="primary" size="sm" onClick={this.handleSave} disabled={saving}>
							{saving ? "Saving…" : editing ? "Update" : "Create"}
						</Button>
					</AccessControl>
                </div>
            </Form>
        )
    }

    render() {
        const { mode, error } = this.state
        return (
            <Modal show onHide={this.props.onClose} backdrop="static" keyboard centered size="lg" className="PayRateModal">
                <Modal.Header>
                    <div className="PayRateModalHeader"><p>Pay Rate History</p></div>
                </Modal.Header>
                <Modal.Body>
                    {error && <Alert variant="danger">{error}</Alert>}
                    {mode === "list" ? this.renderList() : this.renderForm()}
                </Modal.Body>
                <Modal.Footer>
                    <div className="PayRateModalFooter">
                        <Button variant="secondary" onClick={this.props.onClose}>Close</Button>
                    </div>
                </Modal.Footer>
            </Modal>
        )
    }
}

export default PayRateModal
